import React, { useEffect } from 'react';
import ArrowLeftIcon from '@heroicons/react/outline/ArrowLeftIcon.js';
import ChevronRightIcon from '@heroicons/react/outline/ChevronRightIcon.js';
import DocumentTextIcon from '@heroicons/react/outline/DocumentTextIcon.js';
import { PostListFilterByEnum, PostListFilterByOperator, PostStatus, PostTypeContext, } from '@tribeplatform/gql-client/types';
import { EntityImage, getPostIdFromPath, useLogin, useQuery, } from '@tribeplatform/react-components/common';
import { T, useI18n } from '@tribeplatform/react-components/i18n';
import { PostForm } from '@tribeplatform/react-components/Post';
import { useRouter } from '@tribeplatform/react-sdk';
import { useAuthMember, useNetwork, usePost, usePosts, useSpace, } from '@tribeplatform/react-sdk/hooks';
import { Button } from '@tribeplatform/react-ui-kit/Button';
import { Card } from '@tribeplatform/react-ui-kit/Card';
import { List } from '@tribeplatform/react-ui-kit/Layout';
import { Drafts } from './Drafts.js';
import { getTranslatedUpdatePostText, getTranslatedCreatePostText, getTranslatedPostTypeName, } from './translations.js';
export const ComposerBlock = ({ new: isNew, spacePath, postPath, postTypeSlug, }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const intl = useI18n();
    const { Link, push, replace, location } = useRouter();
    const { view, space_id: spaceId } = useQuery() || {};
    const isSpaceSet = !!spacePath || !!spaceId;
    const { data: space } = useSpace({
        fields: {
            authMemberProps: {
                availablePostTypes: 'all',
                spaceMember: {
                    role: 'basic',
                },
            },
            image: 'all',
        },
        variables: spaceId ? { id: spaceId } : { path: spacePath },
        useQueryOptions: { enabled: isSpaceSet },
    });
    const postId = postPath ? getPostIdFromPath(postPath) : null;
    const { data: post, isFetched } = usePost({
        variables: { id: postId },
        useQueryOptions: { enabled: !!postId },
    });
    const { data: member, isGuest } = useAuthMember();
    const { showLogin } = useLogin();
    const { data: { postTypes: networkPostTypes }, } = useNetwork();
    const postType = networkPostTypes.find(postType => postType.id === (postTypeSlug || (post === null || post === void 0 ? void 0 : post.postTypeId)));
    const { data: draftPosts } = usePosts({
        variables: {
            postTypeIds: (postType === null || postType === void 0 ? void 0 : postType.id) ? [postType === null || postType === void 0 ? void 0 : postType.id] : undefined,
            limit: 0,
            filterBy: [
                {
                    keyString: 'status',
                    key: PostListFilterByEnum.publishedAt,
                    operator: PostListFilterByOperator.equals,
                    value: JSON.stringify('DRAFTED'),
                },
                {
                    keyString: 'createdById',
                    key: PostListFilterByEnum.publishedAt,
                    operator: PostListFilterByOperator.equals,
                    value: JSON.stringify(member === null || member === void 0 ? void 0 : member.id),
                },
            ],
        },
        fields: 'basic',
        useInfiniteQueryOptions: {
            enabled: isNew,
        },
    });
    const draftsCount = (_b = (_a = draftPosts === null || draftPosts === void 0 ? void 0 : draftPosts.pages) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.totalCount;
    let availablePostTypes = [];
    if (isSpaceSet) {
        availablePostTypes = (_d = (_c = space === null || space === void 0 ? void 0 : space.authMemberProps) === null || _c === void 0 ? void 0 : _c.availablePostTypes) === null || _d === void 0 ? void 0 : _d.filter(postType => (postType === null || postType === void 0 ? void 0 : postType.context) === PostTypeContext.post);
    }
    else {
        availablePostTypes = networkPostTypes === null || networkPostTypes === void 0 ? void 0 : networkPostTypes.filter(postType => {
            var _a, _b;
            return (postType === null || postType === void 0 ? void 0 : postType.context) === PostTypeContext.post &&
                (postType === null || postType === void 0 ? void 0 : postType.slug) !== 'tribe-message' &&
                ((_b = (_a = postType === null || postType === void 0 ? void 0 : postType.authMemberProps) === null || _a === void 0 ? void 0 : _a.availableSpaces) === null || _b === void 0 ? void 0 : _b.length) > 0;
        });
    }
    const defaultTargetSpace = (_f = (_e = postType === null || postType === void 0 ? void 0 : postType.authMemberProps) === null || _e === void 0 ? void 0 : _e.availableSpaces) === null || _f === void 0 ? void 0 : _f[0];
    const onCancel = () => {
        var _a;
        if (location === null || location === void 0 ? void 0 : location.key) {
            (_a = window === null || window === void 0 ? void 0 : window.history) === null || _a === void 0 ? void 0 : _a.back();
        }
        else {
            push(isNew ? space.relativeUrl || '/' : post === null || post === void 0 ? void 0 : post.relativeUrl);
        }
    };
    useEffect(() => {
        if (isGuest) {
            showLogin(true);
        }
    }, [isGuest, showLogin]);
    if (isGuest) {
        return null;
    }
    if (view === 'drafts') {
        return React.createElement(Drafts, { postTypeId: postType === null || postType === void 0 ? void 0 : postType.id });
    }
    if (isNew && !postTypeSlug) {
        if (availablePostTypes.length === 1) {
            replace(`${isSpaceSet ? `/${space === null || space === void 0 ? void 0 : space.slug}` : ''}/new?post_type=${availablePostTypes[0].id}`);
            return null;
        }
        return (React.createElement(Card, null,
            React.createElement(Card.Header, { title: React.createElement("div", { className: "flex space-s-3 items-center" },
                    React.createElement(Button, { variant: "basic", rounded: true, size: "lg", leadingIcon: React.createElement(ArrowLeftIcon, { className: "rtl:-scale-x-100" }), onClick: onCancel }),
                    React.createElement("span", { className: "text-xl flex-1" },
                        React.createElement(T, { id: "Blocks.Composer.CreateButtonText", defaultMessage: "Create a new" })),
                    draftsCount > 0 && (React.createElement(Button, { variant: "basic", rounded: true, as: Link, href: "?view=drafts", leadingIcon: React.createElement(DocumentTextIcon, null), className: "px-5", size: "lg" },
                        React.createElement(T, { id: "Generics.Drafts.Plural", defaultMessage: "{drafts_count, plural, =0 {No drafts} =1 {# draft} other {# drafts}}", values: { drafts_count: draftsCount } })))) }),
            React.createElement(Card.Content, null,
                React.createElement(List, { divider: true, spacing: "none", className: "border-t border-line-subdued" }, availablePostTypes === null || availablePostTypes === void 0 ? void 0 : availablePostTypes.map(postType => (React.createElement(List.Item, { key: postType === null || postType === void 0 ? void 0 : postType.id },
                    React.createElement(Link, { variant: "inherit", className: "hover:bg-surface-hovered px-4 py-5 sm:p-6 flex space-s-3 items-center", href: `${isSpaceSet ? `/${space === null || space === void 0 ? void 0 : space.slug}` : ''}/new?post_type=${postType.id}` },
                        React.createElement(EntityImage, { size: "xs", entity: { image: postType.icon, name: postType === null || postType === void 0 ? void 0 : postType.name } }),
                        React.createElement("span", { className: "flex-1" }, getTranslatedPostTypeName(intl, postType.name)),
                        React.createElement(ChevronRightIcon, { className: "w-6 h-6 rtl:-scale-x-100" })))))))));
    }
    if (!isNew && !post) {
        return null;
    }
    const postTypeTitle = (post === null || post === void 0 ? void 0 : post.id)
        ? getTranslatedUpdatePostText(intl, postType === null || postType === void 0 ? void 0 : postType.name)
        : getTranslatedCreatePostText(intl, postType === null || postType === void 0 ? void 0 : postType.name);
    return (React.createElement(Card, null,
        React.createElement(Card.Header, { title: React.createElement("div", { className: "flex space-s-3 items-center" },
                React.createElement(Button, { variant: "basic", rounded: true, size: "lg", leadingIcon: React.createElement(ArrowLeftIcon, { className: "rtl:-scale-x-100" }), onClick: onCancel }),
                React.createElement("span", { className: "text-xl flex-1" }, postTypeTitle),
                draftsCount > 0 && (React.createElement(Button, { variant: "basic", rounded: true, as: Link, href: `?view=drafts${(postType === null || postType === void 0 ? void 0 : postType.id) ? `&post_type=${postType === null || postType === void 0 ? void 0 : postType.id}` : ''}`, leadingIcon: React.createElement(DocumentTextIcon, null), className: "px-5", size: "lg" },
                    React.createElement(T, { id: "Generics.Drafts.Plural", defaultMessage: "{drafts_count, plural, =0 {No drafts} =1 {# draft} other {# drafts}}", values: { drafts_count: draftsCount } })))) }),
        React.createElement(Card.Content, null,
            React.createElement(PostForm, { postType: postType, space: isSpaceSet ? space : defaultTargetSpace, availableSpaces: (_g = postType === null || postType === void 0 ? void 0 : postType.authMemberProps) === null || _g === void 0 ? void 0 : _g.availableSpaces, post: post, isPostReady: isFetched, onCancel: onCancel, onSubmit: post => {
                    if ((post === null || post === void 0 ? void 0 : post.status) !== PostStatus.DRAFTED) {
                        replace(post === null || post === void 0 ? void 0 : post.relativeUrl);
                    }
                    else {
                        replace(`${post === null || post === void 0 ? void 0 : post.relativeUrl}/edit`);
                    }
                } }))));
};
//# sourceMappingURL=Block.js.map